import { AppointmentService } from "../../services/AppointmentService";
import { toPatientGender } from "../../utils/patientGender";
import { joinedPhoneOrUndefined, patientEmailFor, verifiedContactFields, } from "./verifiedContact";
import { extractApiError } from "../../utils/extractApiError";
import { generateIdempotencyKey } from "../../utils/idempotencyKey";
import { PaymentService } from "../../services/PaymentService";
import { resolveSystemTypeConfig } from "../../utils/resolveSystemTypeConfig";
import { buildQueueToken, formatDateStr } from "./queueToken";
import { followUpBookingError } from "./followUpSlice";
import { buildBookedAppointment, buildBookedRecord, queueVisitCharge, } from "./bookedAppointment";
import { quoteCharge, requireQuotedCharge } from "./chargeQuote";
import { convertISTToTimezone as _tz } from "../../components/appointment/utils/date";
const POPUP_TARGET = "medos_online_payment";
const POPUP_FEATURES = "toolbar=no,location=no,directories=no,status=no,menubar=no,scrollbars=yes,resizable=yes,width=520,height=720";
export const ONLINE_PAYMENT_POLL_INTERVAL_MS = 3000;
export const ONLINE_PAYMENT_POLL_TIMEOUT_MS = 5 * 60 * 1000;
function buildPatientPayload(state) {
    const { patientTab, selectedPatient, newPatientForm } = state;
    const contact = verifiedContactFields(state);
    if (patientTab === "existing" && selectedPatient) {
        return {
            id: selectedPatient.id,
            firstName: selectedPatient.firstName,
            lastName: selectedPatient.lastName || "",
            email: patientEmailFor(state, selectedPatient.email),
            ...contact,
            age: selectedPatient.age || 0,
            gender: toPatientGender(selectedPatient.gender),
        };
    }
    const nameParts = newPatientForm.fullName.trim().split(/\s+/);
    return {
        firstName: nameParts[0] || "",
        lastName: nameParts.slice(1).join(" ") || "",
        email: patientEmailFor(state, newPatientForm.email),
        ...contact,
        age: newPatientForm.age ? parseInt(newPatientForm.age, 10) : 0,
        gender: toPatientGender(newPatientForm.gender),
    };
}
function buildPatientAddress(state) {
    const { patientTab, newPatientForm, countryCode, otpChannel } = state;
    const city = patientTab === "new" ? newPatientForm.city.trim() || "N/A" : "N/A";
    return {
        addressLine1: patientTab === "new" ? newPatientForm.address || "" : "",
        city,
        state: "",
        country: otpChannel !== "email" && countryCode === "+91" ? "India" : "",
        zipcode: patientTab === "new" ? newPatientForm.zipCode || "" : "",
        ...verifiedContactFields(state),
    };
}
function buildReservePayload(state) {
    if (resolveSystemTypeConfig(state.activeSystemType).bookingMode === "qms") {
        return buildQmsReservePayload(state);
    }
    const { workspace, selectedDoctor, selectedAddressId, consultationMode, selectedDate, selectedSlot, selectedTimezone, selectedPack, } = state;
    if (!workspace || !selectedDoctor || !selectedAddressId || !selectedSlot) {
        throw new Error("Missing required booking data");
    }
    void _tz;
    const dateStr = formatDateStr(selectedDate);
    const fromDateTimeTs = selectedSlot.start.includes("T")
        ? selectedSlot.start
        : `${dateStr}T${selectedSlot.start}:00`;
    const toDateTimeTs = selectedSlot.end.includes("T")
        ? selectedSlot.end
        : `${dateStr}T${selectedSlot.end}:00`;
    const charge = requireQuotedCharge(state);
    if (charge.kind === "ACTIVE_PACK") {
        throw new Error("A session drawn from an owned pack has nothing to pay.");
    }
    const payload = {
        workspaceId: workspace.workspaceId,
        workspaceAddressId: selectedAddressId,
        addressId: selectedAddressId,
        doctorId: selectedDoctor.id,
        mode: consultationMode,
        appointmentDate: dateStr,
        fromDateTimeTs,
        toDateTimeTs,
        consultationCharge: charge.amount,
        bookingType: charge.kind === "PACK_PURCHASE"
            ? "PACKAGE_PURCHASE"
            : "ONE_TIME_APPOINTMENT",
        paymentMode: "CARD",
        patientTimezone: selectedTimezone,
        patientPayload: buildPatientPayload(state),
        patientAddress: buildPatientAddress(state),
        currency: charge.currency,
        source: "SDK_POWERED_WEBSITE",
        type: "CONSULTATION",
        paymentAmount: charge.amount,
    };
    if (charge.kind === "PACK_PURCHASE" && selectedPack) {
        payload.packageConfigId = selectedPack.id;
        payload.packageAmount = charge.amount;
    }
    else if (charge.kind === "FOLLOW_UP") {
        payload.followUp = true;
    }
    return payload;
}
function buildQmsReservePayload(state) {
    const { workspace, selectedDoctor, selectedAddressId, selectedDate, selectedSlot, } = state;
    if (!workspace || !selectedDoctor || !selectedAddressId || !selectedSlot) {
        throw new Error("Missing required QMS booking data");
    }
    return {
        workspaceId: workspace.workspaceId,
        addressId: selectedAddressId,
        doctorId: selectedDoctor.id,
        qmsWorkShiftId: Number(selectedSlot.id),
        appointmentDate: formatDateStr(selectedDate),
        mode: "OFFLINE",
        bookingChannel: "SELF_QR",
        patientPhoneNumber: joinedPhoneOrUndefined(state),
        isInternational: state.feeAudience === "INTERNATIONAL",
        source: "SDK_POWERED_WEBSITE",
        patientPayload: buildPatientPayload(state),
        patientAddress: buildPatientAddress(state),
    };
}
function isGatewayUnavailable(e) {
    const reason = e?.response
        ?.data?.reason;
    return reason === "GATEWAY_NOT_CONFIGURED";
}
function resolveReturnUrl() {
    if (typeof window !== "undefined" && window.location) {
        return window.location.href;
    }
    return "https://medos.one/";
}
function tryOpenCheckoutPopup(url) {
    if (typeof window === "undefined")
        return null;
    try {
        return window.open(url, POPUP_TARGET, POPUP_FEATURES);
    }
    catch {
        return null;
    }
}
export const createPaymentSlice = (set, get) => ({
    paymentStatus: "idle",
    paymentSessionRef: null,
    paymentAppointmentRef: null,
    paymentCheckoutUrl: null,
    paymentError: null,
    paymentPopup: null,
    paymentUnavailable: false,
    paymentFailureReason: null,
    availablePaymentMethods: [],
    selectedPaymentMethod: { kind: "AT_CLINIC" },
    claimStatus: "idle",
    claimError: null,
    claimReference: null,
    claimProofUrl: null,
    claimIdempotencyKey: null,
    qmsIdempotencyKey: null,
    claimDraftReference: "",
    claimDraftProof: null,
    claimDraftProofError: null,
    startOnlinePayment: async () => {
        const state = get();
        if (state.paymentStatus === "reserving" ||
            state.paymentStatus === "awaiting") {
            return;
        }
        set({
            paymentStatus: "reserving",
            paymentError: null,
            paymentSessionRef: null,
            paymentAppointmentRef: null,
            paymentCheckoutUrl: null,
            paymentPopup: null,
            bookingError: null,
            paymentFailureReason: null,
            currentStep: "payment-pending",
        });
        try {
            const payload = buildReservePayload(state);
            const returnUrl = resolveReturnUrl();
            const idempotencyKey = generateIdempotencyKey();
            const reservation = await AppointmentService.reserveOnlinePayment(payload, returnUrl, idempotencyKey);
            if (get().paymentStatus !== "reserving") {
                return;
            }
            const sessionRef = reservation.sessionRef ?? null;
            const checkoutUrl = reservation.checkoutUrl ?? null;
            const visit = reservation.visit;
            const appointmentRef = visit?.tokenDisplay ??
                reservation.appointmentRef ??
                reservation.bookingReference ??
                null;
            const reservedAppointmentId = typeof visit?.id === "number"
                ? visit.id
                : typeof reservation.appointmentId === "number"
                    ? reservation.appointmentId
                    : typeof reservation.id === "number"
                        ? reservation.id
                        : null;
            if (!sessionRef || !checkoutUrl) {
                throw new Error("Online payment is not available for this booking. Please choose Pay at Clinic.");
            }
            const queueToken = visit
                ? buildQueueToken(state, {
                    id: visit.id,
                    tokenNumber: visit.tokenNumber,
                    tokenDisplay: visit.tokenDisplay,
                    queuePosition: visit.position,
                })
                : null;
            set({
                paymentStatus: "awaiting",
                paymentSessionRef: sessionRef,
                ...(queueToken ? { queueToken } : {}),
                bookedAppointment: (() => {
                    if (!("qmsWorkShiftId" in payload)) {
                        return buildBookedAppointment(state, payload);
                    }
                    const charge = queueVisitCharge(quoteCharge(state), visit);
                    return charge ? buildBookedRecord(state, charge) : null;
                })(),
                paymentAppointmentRef: appointmentRef,
                paymentCheckoutUrl: checkoutUrl,
                currentStep: "payment-pending",
                ...(reservedAppointmentId !== null
                    ? { appointmentId: reservedAppointmentId }
                    : {}),
            });
            const popup = tryOpenCheckoutPopup(checkoutUrl);
            if (popup) {
                set({ paymentPopup: popup });
            }
        }
        catch (e) {
            if (get().paymentStatus !== "reserving") {
                return;
            }
            if (isGatewayUnavailable(e)) {
                set({
                    paymentStatus: "failed",
                    paymentUnavailable: true,
                    paymentError: "Online payment isn't available for this clinic. Please continue with Pay at Clinic.",
                });
                return;
            }
            set({
                paymentStatus: "failed",
                paymentError: followUpBookingError(e) ??
                    extractApiError(e, "Could not start online payment. Please try again."),
            });
        }
    },
    resumeOnlinePayment: () => {
        const { paymentSessionRef, paymentCheckoutUrl } = get();
        if (!paymentSessionRef || !paymentCheckoutUrl)
            return;
        const popup = tryOpenCheckoutPopup(paymentCheckoutUrl);
        set({
            paymentStatus: "awaiting",
            paymentError: null,
            paymentPopup: popup,
            currentStep: "payment-pending",
        });
    },
    finishOnlinePayment: (result) => {
        const current = get();
        const popup = current.paymentPopup;
        if (popup && !popup.closed) {
            try {
                popup.close();
            }
            catch {
            }
        }
        switch (result.status) {
            case "success": {
                set({
                    paymentStatus: "success",
                    paymentError: null,
                    paymentFailureReason: null,
                    paymentPopup: null,
                    currentStep: resolveSystemTypeConfig(current.activeSystemType)
                        .confirmationStep,
                    appointmentId: result.appointmentId ?? current.appointmentId ?? null,
                });
                get().bumpBookingRevision();
                return;
            }
            case "failed": {
                const reason = result.reason ?? null;
                if (reason === "APPOINTMENT_ALREADY_PAID") {
                    set({
                        paymentStatus: "success",
                        paymentError: null,
                        paymentFailureReason: null,
                        paymentPopup: null,
                        currentStep: resolveSystemTypeConfig(current.activeSystemType)
                            .confirmationStep,
                    });
                    get().bumpBookingRevision();
                    return;
                }
                set({
                    paymentStatus: "failed",
                    paymentFailureReason: reason,
                    paymentError: result.message ??
                        "Payment could not be verified. Please try again.",
                    paymentPopup: null,
                });
                return;
            }
            case "timeout":
                set({
                    paymentStatus: "timeout",
                    paymentError: "We're still waiting on your bank to confirm this payment. It can take a few minutes.",
                    paymentPopup: null,
                });
                return;
            case "cancelled":
            default:
                set({
                    paymentStatus: "cancelled",
                    paymentError: "Payment cancelled.",
                    paymentPopup: null,
                });
        }
    },
    recheckOnlinePayment: async () => {
        const { paymentSessionRef, paymentStatus } = get();
        if (!paymentSessionRef)
            return;
        if (paymentStatus === "reserving" || paymentStatus === "awaiting")
            return;
        try {
            const res = await AppointmentService.confirmOnlinePayment({
                sessionRef: paymentSessionRef,
            });
            if (res.status === "SUCCESS") {
                get().finishOnlinePayment({ status: "success" });
                return;
            }
            if (res.status === "FAILED") {
                get().finishOnlinePayment({
                    status: "failed",
                    message: res.message,
                    reason: res.reason,
                });
                return;
            }
        }
        catch (e) {
            set({
                paymentError: extractApiError(e, "We couldn't reach the payment service. Please try again in a moment."),
            });
        }
    },
    setPaymentPopup: (popup) => set({ paymentPopup: popup }),
    resetOnlinePayment: () => {
        const popup = get().paymentPopup;
        if (popup && !popup.closed) {
            try {
                popup.close();
            }
            catch {
            }
        }
        set({
            paymentStatus: "idle",
            paymentSessionRef: null,
            paymentAppointmentRef: null,
            paymentCheckoutUrl: null,
            appointmentId: null,
            queueToken: null,
            bookedAppointment: null,
            paymentError: null,
            paymentFailureReason: null,
            paymentPopup: null,
        });
    },
    setAvailablePaymentMethods: (methods) => {
        const current = get().selectedPaymentMethod;
        const stillThere = current.kind !== "MANUAL" ||
            methods.some((m) => m.id === current.methodId);
        set({
            availablePaymentMethods: methods,
            selectedPaymentMethod: stillThere ? current : { kind: "AT_CLINIC" },
        });
    },
    setPaymentMethod: (choice) => set({ selectedPaymentMethod: choice }),
    setClaimDraftReference: (reference) => set({ claimDraftReference: reference }),
    setClaimDraftProof: (file) => {
        if (!file) {
            set({ claimDraftProof: null, claimDraftProofError: null });
            return;
        }
        const invalid = PaymentService.validateProofFile(file);
        set({
            claimDraftProof: invalid ? null : file,
            claimDraftProofError: invalid,
        });
    },
    resetClaimDraft: () => set({
        claimDraftReference: "",
        claimDraftProof: null,
        claimDraftProofError: null,
    }),
    beginPaymentClaim: () => set({
        claimStatus: "idle",
        claimError: null,
        claimReference: null,
        claimProofUrl: null,
        claimIdempotencyKey: generateIdempotencyKey(),
    }),
    submitPaymentClaim: async ({ appointmentId, paymentMethodId, provider, paymentReference, amount, currency, proofFile, }) => {
        const { claimStatus, claimIdempotencyKey } = get();
        if (claimStatus === "uploading" || claimStatus === "submitting")
            return;
        const idempotencyKey = claimIdempotencyKey ?? generateIdempotencyKey();
        set({ claimError: null, claimIdempotencyKey: idempotencyKey });
        try {
            let proofUrl = get().claimProofUrl;
            if (proofFile) {
                const invalid = PaymentService.validateProofFile(proofFile);
                if (invalid) {
                    set({ claimStatus: "failed", claimError: invalid });
                    return;
                }
                set({ claimStatus: "uploading" });
                proofUrl = await PaymentService.uploadPaymentProof(appointmentId, proofFile);
                set({ claimProofUrl: proofUrl });
            }
            set({ claimStatus: "submitting" });
            await PaymentService.submitPaymentClaim(appointmentId, {
                paymentMethodId,
                provider: provider ?? null,
                paymentReference: paymentReference?.trim() || null,
                amount: amount ?? null,
                currency: currency ?? null,
                proofUrl: proofUrl ?? null,
            }, idempotencyKey);
            set({
                claimStatus: "submitted",
                claimReference: paymentReference?.trim() || null,
                claimError: null,
                claimDraftReference: "",
                claimDraftProof: null,
                claimDraftProofError: null,
            });
        }
        catch (e) {
            set({
                claimStatus: "failed",
                claimError: extractApiError(e, "We couldn't record your payment. Please try again."),
            });
        }
    },
    resetPaymentClaim: () => set({
        claimStatus: "idle",
        claimError: null,
        claimReference: null,
        claimProofUrl: null,
        claimIdempotencyKey: null,
        claimDraftReference: "",
        claimDraftProof: null,
        claimDraftProofError: null,
    }),
});
